import { Routes } from '@angular/router';
import { APP_ROUTES } from './core/constants/app-routes.const';
import { PERMISSION_IDS } from './core/constants/permission.const';
import { authGuard } from './core/guards/auth.guard';
import { guestGuard } from './core/guards/guest.guard';
import { permissionGuard } from './core/guards/permission.guard';
import { MainLayoutComponent } from './core/layout/main-layout.component';
import { LandingComponent } from './features/auth/landing/landing.component';
import { Error403Component } from './shared/components/error-403/error-403.component';

export const routes: Routes = [
  {
    path: APP_ROUTES.LOGIN,
    canActivate: [guestGuard],
    loadComponent: () =>
      import('./features/auth/login/login.component').then(
        (m) => m.LoginComponent,
      ),
  },
  {
    path: APP_ROUTES.FORGOT_PASSWORD,
    canActivate: [guestGuard],
    loadComponent: () =>
      import('./features/auth/forgot-password/forgot-password.component').then(
        (m) => m.ForgotPasswordComponent,
      ),
  },
  {
    path: APP_ROUTES.ROOT,
    component: MainLayoutComponent,
    canActivate: [authGuard],
    children: [
      {
        path: APP_ROUTES.ROOT,
        component: LandingComponent,
        canActivate: [permissionGuard(PERMISSION_IDS.INIT)],
        pathMatch: 'full',
      },
      {
        path: APP_ROUTES.DASHBOARD,
        canActivate: [permissionGuard(PERMISSION_IDS.DASHBOARD)],
        loadComponent: () =>
          import('./features/dashboard/dashboard.component').then(
            (m) => m.DashboardComponent,
          ),
      },
      {
        path: APP_ROUTES.ACTIVITY_LOG,
        canActivate: [permissionGuard(PERMISSION_IDS.ACTIVITY_LOG)],
        loadComponent: () =>
          import('./features/activity-log/activity-log-list/activity-log.component').then(
            (m) => m.ActivityLogComponent,
          ),
      },
      {
        path: APP_ROUTES.KPI_CAMERA,
        canActivate: [permissionGuard(PERMISSION_IDS.KPI_CAMERA)],
        loadComponent: () =>
          import('./features/kpi-report/kpi-report-list/kpi-report.component').then(
            (m) => m.KpiReportComponent,
          ),
      },
      {
        path: APP_ROUTES.WAREHOUSE,
        canActivate: [permissionGuard(PERMISSION_IDS.WAREHOUSE_MGT)],
        loadComponent: () =>
          import('./features/management-warehouse/warehouses/warehouses-list/warehouses.component').then(
            (m) => m.WarehousesComponent,
          ),
      },
      {
        path: APP_ROUTES.HISTORY_WAREHOUSE,
        canActivate: [permissionGuard(PERMISSION_IDS.WAREHOUSE_HISTORY)],
        loadComponent: () =>
          import('./features/management-warehouse/history-warehouse-transfer/history-warehouse-list/history-warehouse-list.component').then(
            (m) => m.WarehouseHistoryComponent,
          ),
      },
      {
        path: APP_ROUTES.WAREHOUSE_REPORT,
        canActivate: [permissionGuard(PERMISSION_IDS.IMPORT_EXPORT_REPORT)],
        loadComponent: () =>
          import('./features/warehouse-report/warehouse-report-list/warehouse-report.component').then(
            (m) => m.WarehouseReportComponent,
          ),
      },
      {
        path: APP_ROUTES.PLATFORM_FEE,
        canActivate: [permissionGuard(PERMISSION_IDS.PLATFORM_FEE_MGT)],
        loadComponent: () =>
          import('./features/management-platfrom-fee/management-platform-fee-list/management-platform-fee-list.component').then(
            (m) => m.PlatformFeeComponent,
          ),
      },
      {
        path: APP_ROUTES.SERVICE,
        canActivate: [permissionGuard(PERMISSION_IDS.SERVICE_MGT)],
        loadComponent: () =>
          import('./features/management-services/service-list/service-list.component').then(
            (m) => m.ServiceComponent,
          ),
      },
      {
        path: APP_ROUTES.KPI_CAMERA,
        canActivate: [permissionGuard(PERMISSION_IDS.KPI_CAMERA)],
        loadComponent: () =>
          import('./features/kpi-report/kpi-report-list/kpi-report.component').then(
            (m) => m.KpiReportComponent,
          ),
      },
      {
        path: APP_ROUTES.DEVICE_TYPE,
        canActivate: [permissionGuard(PERMISSION_IDS.DEVICE_TYPE_MGT)],
        loadComponent: () =>
          import('./features/device-type-management/device-type-list/device-type-list.component').then(
            (m) => m.DeviceTypeListComponent,
          ),
      },
      {
        path: APP_ROUTES.SIM,
        canActivate: [permissionGuard(PERMISSION_IDS.SIM_CARD_MGT)],
        loadComponent: () =>
          import('./features/sim-management/sim-list/sim-list.component').then(
            (m) => m.SimListComponent,
          ),
      },
      {
        path: APP_ROUTES.DEVICE,
        canActivate: [permissionGuard(PERMISSION_IDS.DEVICE_MGT)],
        loadComponent: () =>
          import('./features/device-management/device-list/device-list.component').then(
            (m) => m.DeviceListComponent,
          ),
      },
      {
        path: APP_ROUTES.USERS,
        canActivate: [permissionGuard(PERMISSION_IDS.USER_MGT)],
        loadComponent: () =>
          import('./features/user-management/user-list/user-list-management.component').then(
            (m) => m.UserListManagementComponent,
          ),
      },
      {
        path: APP_ROUTES.API_SETTINGS,
        canActivate: [permissionGuard(PERMISSION_IDS.API_SETTINGS)],
        loadComponent: () =>
          import('./features/api-settings/api-settings-page/api-settings.component').then(
            (m) => m.ApiSettingsComponent,
          ),
      },
      {
        path: APP_ROUTES.SETTINGS,
        canActivate: [permissionGuard(PERMISSION_IDS.SYSTEM_SETTINGS)],
        children: [
          {
            path: APP_ROUTES.EMPLOYEES,
            canActivate: [permissionGuard(PERMISSION_IDS.HR_MGT)],
            loadComponent: () =>
              import('./features/settings/employees/employees-list/employees.component').then(
                (m) => m.EmployeesComponent,
              ),
          },
          {
            path: APP_ROUTES.PERMISSION_GROUPS,
            canActivate: [permissionGuard(PERMISSION_IDS.PERMISSION_GROUP_MGT)],
            loadComponent: () =>
              import('./features/settings/permission-groups/permission-groups-list/permission-groups.component').then(
                (m) => m.PermissionGroupsComponent,
              ),
          },
          {
            path: APP_ROUTES.PERMISSIONS,
            canActivate: [
              permissionGuard(PERMISSION_IDS.FUNCTION_PERMISSION_MGT),
            ],
            loadComponent: () =>
              import('./features/settings/permission-assignment/permission-assignment').then(
                (m) => m.PermissionAssignment,
              ),
          },
        ],
      },
    ],
  },
  {
    path: APP_ROUTES.FORBIDDEN,
    component: Error403Component,
  },
  {
    path: '**',
    redirectTo: APP_ROUTES.ROOT,
  },
];
